/**
 * "Who books this hotel" — the top customers by volume, at the bottom of the
 * hotel panel.
 *
 * Feedback log item 8, with item 20's columns: "most booked clients listed and
 * RACs booked and number of room nights and reservations and average nightly
 * rate and commission yield for each".
 *
 * ── Why this fetches, when nothing else in the panel does ──────────────────
 * Every other section is a pure function of what `/api/btp/hotels` already sent.
 * This one reads Domo, so it is the only part that can be slow or fail — and it
 * is fetched ONLY when a row is expanded, so a Domo outage costs one collapsed
 * section rather than the Hotels table.
 *
 * That is also why the loading and error states live here rather than in the
 * panel: the rest of the panel must keep rendering while this is in flight.
 *
 * ── The numbers are stated, not implied ────────────────────────────────────
 * Two of these columns are easy to misread, so neither is shown bare:
 *
 *   • ADR is nights-weighted (spend ÷ nights), not the mean of per-booking
 *     rates, and it is blank rather than 0 when there were no nights.
 *   • Commission is the average of the percentages the feed REPORTED, over a
 *     sample that is usually smaller than the booking count — most consortium
 *     bookings are non-commissionable. So the sample size is shown next to it.
 *     A bare "10%" would read as expected yield, which it is not.
 */

import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';

export interface CustomerVolumeRow {
  client_name: string;
  account_number: string | null;
  reservations: number;
  nights: number;
  room_spend: number;
  adr: number | null;
  racs: { code: string; program: string; label: string; nights: number }[];
  commission_pct_avg: number | null;
  commission_pct_samples: number;
  commissionable: number;
  non_commissionable: number;
  commission_unknown: number;
}

export interface CustomerVolumeResponse {
  property_key: string;
  property_name: string | null;
  match: 'gds_code' | 'name_city_state';
  window: { from: string; to: string; months: number };
  customers: CustomerVolumeRow[];
  total_customers: number;
  truncated: boolean;
}

/** What the panel knows about the property, in the shape the endpoint wants. */
export interface PropertyIdentity {
  gdsCodes: (string | null)[];
  name: string;
  city: string | null;
  state: string | null;
}

/**
 * Is this the payload we asked for?
 *
 * Checked rather than assumed because this section renders INSIDE the hotel
 * panel: reading `data.window.months` off an unexpected shape throws during
 * render, React unmounts the whole tree, and the entire Hotels page goes blank —
 * a section that cannot load taking down the page it lives on. Caught by the
 * existing HotelsPage expander suite, which returns one blanket response for
 * every endpoint.
 *
 * A shape we do not recognise is treated exactly like a failed request: this
 * section shows a banner, everything else keeps rendering.
 */
function isVolumeResponse(d: unknown): d is CustomerVolumeResponse {
  if (!d || typeof d !== 'object') return false;
  const o = d as Partial<CustomerVolumeResponse>;
  return (
    Array.isArray(o.customers) &&
    typeof o.total_customers === 'number' &&
    !!o.window &&
    typeof o.window.months === 'number'
  );
}

const fmtNum = (n: number) => n.toLocaleString();
const fmtMoney = (n: number | null) =>
  n == null
    ? '—'
    : `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export function HotelCustomerVolume({
  property,
  limit = 5,
}: {
  property: PropertyIdentity;
  limit?: number;
}) {
  const { can } = useMe();
  // Same capability the endpoint enforces. Checked here so a reader without it
  // is TOLD why the section is empty rather than being shown a 403 banner that
  // reads as a broken feature — and so we do not fire a request that is
  // guaranteed to fail on every hotel row csm and viewer open.
  //
  // The client check is convenience, never the boundary: the gate that matters
  // is `requireCapability` on the route.
  const canSee = can('view:supplier_rate_opportunities');

  const [data, setData] = useState<CustomerVolumeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const codes = property.gdsCodes.filter(Boolean).join(',');

  useEffect(() => {
    if (!canSee) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const qs = new URLSearchParams({ limit: String(limit) });
    if (codes) qs.set('gds_codes', codes);
    if (property.name) qs.set('name', property.name);
    if (property.city) qs.set('city', property.city);
    if (property.state) qs.set('state', property.state);

    apiFetch<CustomerVolumeResponse>(`/api/btp/hotels/customers?${qs}`)
      .then((d) => {
        if (cancelled) return;
        if (!isVolumeResponse(d)) {
          setError('Booking volume came back in a shape we could not read.');
          return;
        }
        setData(d);
      })
      .catch((err) => {
        if (cancelled) return;
        // The API's own message, verbatim — it carries the failure kind and a
        // ref that ties the banner to the log line.
        setError(
          err instanceof ApiError
            ? err.message
            : 'Could not load booking volume for this property.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canSee, codes, property.name, property.city, property.state, limit]);

  return (
    <div className="section" data-testid="hotel-detail-customer-volume">
      <div className="section-header">
        <span className="section-label">Who books this hotel</span>
        <span className="section-meta">
          {data
            ? `top ${data.customers.length} of ${fmtNum(data.total_customers)} · last ${data.window.months} months`
            : 'by room nights'}
        </span>
      </div>
      <div className="section-body">
        {/* Says why, rather than rendering nothing. An absent section reads as a
            bug or as something you have not found; this reads as a permission —
            the same choice LogContactForm makes for write:supplier. */}
        {!canSee && (
          <p className="page-intro" data-testid="hotel-customer-volume-denied">
            Booking volume by customer is limited to Hotel Supplier Managers and
            admins.
          </p>
        )}

        {canSee && loading && (
          <p className="page-intro" data-testid="hotel-customer-volume-loading">
            Loading booking volume…
          </p>
        )}

        {canSee && error && (
          <div
            className="banner banner-error"
            data-testid="hotel-customer-volume-error"
          >
            {error}
          </div>
        )}

        {canSee &&
          !loading &&
          !error &&
          data &&
          data.customers.length === 0 && (
            <p className="page-intro" data-testid="hotel-customer-volume-empty">
              No bookings on record for this property in the last{' '}
              {data.window.months} months.
            </p>
          )}

        {canSee && !loading && !error && data && data.customers.length > 0 && (
          <>
            <div className="table-scroll">
              <table
                className="data-table"
                data-testid="hotel-customer-volume-table"
              >
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>RACs booked</th>
                    <th>Room nights</th>
                    <th>Reservations</th>
                    <th>Avg nightly</th>
                    <th>Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {data.customers.map((c) => (
                    <tr key={c.account_number ?? c.client_name}>
                      <td>{c.client_name}</td>
                      <td>
                        {c.racs.length === 0 ? (
                          '—'
                        ) : (
                          <span
                            title={c.racs
                              .map((r) => `${r.code} — ${r.label}`)
                              .join('\n')}
                          >
                            {c.racs.map((r) => r.code).join(', ')}
                          </span>
                        )}
                      </td>
                      <td>{fmtNum(c.nights)}</td>
                      <td>{fmtNum(c.reservations)}</td>
                      <td>{fmtMoney(c.adr)}</td>
                      <td>
                        {c.commission_pct_avg == null ? (
                          '—'
                        ) : (
                          <>
                            {c.commission_pct_avg}%{' '}
                            {/* The sample size travels with the number: this is
                                the average of what was REPORTED, not a yield
                                over every booking. */}
                            <span className="text-muted">
                              ({c.commission_pct_samples} of {c.reservations})
                            </span>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Said on screen, not just in a comment — whoever reads this table
                is the person who would otherwise over-trust these two columns. */}
            <p
              className="page-intro"
              style={{ marginTop: 10, marginBottom: 0 }}
            >
              Avg nightly is spend ÷ room nights. Commission is the average of
              the rates actually reported on those bookings, not an expected
              yield — most consortium bookings are non-commissionable.
              {data.match === 'name_city_state' && (
                <>
                  {' '}
                  This property has no GDS code on file, so its bookings were
                  matched by name and city.
                </>
              )}
              {data.truncated && (
                <>
                  {' '}
                  <strong>
                    This property has more bookings than one fetch returns, so
                    these totals are partial.
                  </strong>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
